import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { NEUTRAL_TENANT_ACCENT } from "@lms/backend/lib/design/accent";
import { TenantNav } from "@/components/tenant-nav";
import { TenantScopeShell } from "@/components/tenant-scope-shell";
import { TenantSlugGuard } from "@/components/tenant-slug-guard";
import { TenantFooter } from "@/components/tenant-footer";
import { TermsGate } from "@/components/terms-gate";
import { PageShell, Heading, Text } from "@/components/ui";
import { fetchTenantBootstrap, postSessionHeartbeat } from "@/services/api/tenants";
import { useAuthStore } from "@/stores/authStore";
import { rememberServerIdleLimit } from "@/hooks/use-session-idle-timeout";

export function TenantLayout() {
  const { tenantSlug = "" } = useParams<{ tenantSlug: string }>();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const t = useTranslations("tenantPublic");

  const bootstrapQuery = useQuery({
    queryKey: ["tenant-bootstrap", tenantSlug],
    queryFn: () => fetchTenantBootstrap(tenantSlug),
    enabled: Boolean(tenantSlug),
  });

  useEffect(() => {
    if (!isAuthenticated) return;
    // SPA-4: its answer carries the server's idle limit — keep it for the idle timer.
    void postSessionHeartbeat()
      .then(rememberServerIdleLimit)
      .catch(() => undefined);
  }, [isAuthenticated, tenantSlug]);

  if (bootstrapQuery.isLoading) {
    return (
      <TenantScopeShell testId="tenant-layout-loading">
        <PageShell pad="none" className="flex flex-1 items-center justify-center py-16">
          <Text tone="muted">{t("loading")}</Text>
        </PageShell>
      </TenantScopeShell>
    );
  }

  if (bootstrapQuery.isError || !bootstrapQuery.data) {
    return (
      <TenantScopeShell testId="tenant-not-found">
        <PageShell>
          <Heading as="h1" level="page">
            {t("tenantNotFound")}
          </Heading>
          <Text tone="muted" className="mt-2">
            {t("tenantNotFoundHint")}
          </Text>
        </PageShell>
      </TenantScopeShell>
    );
  }

  const { tenant } = bootstrapQuery.data;
  const accent = tenant.branding.accentColor ?? NEUTRAL_TENANT_ACCENT;

  return (
    <TenantScopeShell accent={accent}>
      <TenantNav
        tenantSlug={tenant.slug}
        isAuthenticated={isAuthenticated}
        userRole={user?.role}
        tenantName={tenant.name}
        logoUrl={tenant.logoUrl}
      />
      <div className="flex w-full flex-1 flex-col pb-24 pt-2 sm:pb-28">
        <TenantSlugGuard tenantSlug={tenant.slug}>
          {/* H4: the Terms of use + Privacy notice are accepted before anything else. */}
          <TermsGate tenantSlug={tenant.slug} schoolName={tenant.name}>
            <Outlet />
          </TermsGate>
        </TenantSlugGuard>
      </div>
      <TenantFooter tenantSlug={tenant.slug} tenantName={tenant.name} logoUrl={tenant.logoUrl} />
    </TenantScopeShell>
  );
}
